import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import useMeta from "@/hooks/useMeta";
import { Card, Input, Select, Spinner, EmptyState, Modal, Button } from "@/components/UI";
import { ListOrdered, Search, Flag, CheckCircle2 } from "lucide-react";

const MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

export default function SprintList() {
  const navigate = useNavigate();
  const meta = useMeta();
  const now = new Date();
  const [sprints, setSprints] = useState(null);
  const [search, setSearch] = useState("");
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [endTarget, setEndTarget] = useState(null);
  const [ending, setEnding] = useState(false);

  const years = useMemo(() => {
    const ys = new Set((meta.sprints || []).map((s) => s.year).filter(Boolean));
    ys.add(now.getFullYear());
    return [...ys].sort((a, b) => b - a);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.sprints]);

  const load = () =>
    api
      .get("/sprints", {
        params: {
          stats: 1,
          year: year || undefined,
          month: month || undefined,
          search: search.trim() || undefined,
        },
      })
      .then((r) => setSprints(r.data))
      .catch(() => setSprints([]));

  useEffect(() => {
    const t = setTimeout(() => {
      setSprints(null);
      load();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, search]);

  const confirmEndSprint = async () => {
    if (!endTarget) return;
    setEnding(true);
    try {
      await api.post("/sprints/end-active");
      toast.success("Sprint diakhiri — sprint planning berikutnya sekarang aktif");
      setEndTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal mengakhiri sprint");
      setEndTarget(null);
    } finally {
      setEnding(false);
    }
  };

  // Sprint planning disembunyikan — baru muncul setelah berubah jadi active
  const visible = useMemo(() => (sprints || []).filter((s) => s.status !== "planning"), [sprints]);

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="sprint-list-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">List Sprint</h1>
        <p className="text-sm text-slate-400">Riwayat & rekap semua sprint. Klik sprint untuk melihat task di dalamnya.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            className="pl-9"
            placeholder="Cari sprint, task, project, atau PIC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="sprint-search"
          />
        </div>
        <Select className="w-32" value={year} onChange={(e) => setYear(e.target.value)} data-testid="sprint-filter-year">
          <option value="">Semua Tahun</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
        <Select className="w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="sprint-filter-month">
          <option value="">Semua Bulan</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </Select>
      </div>

      {!sprints ? (
        <Spinner />
      ) : visible.length === 0 ? (
        <EmptyState icon={ListOrdered} title="Tidak ada sprint" subtitle="Coba ubah filter tahun/bulan atau kata kunci pencarian." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {visible.map((s) => {
            const isActive = s.status === "active";
            return (
              <Card
                key={s.id}
                className="cursor-pointer p-5 transition-colors hover:border-slate-500/40"
                onClick={() => navigate(`/sprints/${s.id}`)}
                data-testid={`sprint-card-${s.id}`}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <h3 className="font-heading text-lg font-semibold text-slate-100">{s.name}</h3>
                  <span
                    className={`mt-1 flex h-3 w-3 shrink-0 rounded-full ${isActive ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,.7)]" : "bg-slate-500"}`}
                    title={isActive ? "Active" : "Completed"}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  {s.start_date ? new Date(s.start_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "—"}
                  {" – "}
                  {s.end_date ? new Date(s.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </p>

                <div className="mt-4 flex items-center gap-1.5 text-sm text-slate-300">
                  <CheckCircle2 size={15} className="text-emerald-400" />
                  <span className="font-mono tabular-nums">{s.done_tasks ?? 0}/{s.task_count ?? 0} task selesai</span>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className="font-mono text-xs tabular-nums text-slate-500">{s.completed_weight ?? 0}/{s.total_weight ?? 0} pts</span>
                  {isActive && (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={(e) => { e.stopPropagation(); setEndTarget(s); }}
                      data-testid={`end-sprint-${s.id}`}
                    >
                      <Flag size={13} /> End Sprint
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={!!endTarget}
        onClose={() => setEndTarget(null)}
        title="Akhiri Sprint"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEndTarget(null)}>Batal</Button>
            <Button variant="danger" onClick={confirmEndSprint} disabled={ending} data-testid="confirm-end-sprint">
              {ending ? "Memproses..." : "Ya, Akhiri Sprint"}
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Akhiri sprint <strong>{endTarget?.name}</strong>?
        </p>
      </Modal>
    </div>
  );
}
